import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useMedia } from "@/hooks/use-media";
import { cn } from "@/lib/utils";
import UsersTab from "@/components/settings/UsersTab";
import FinanziamentoTab from "@/components/settings/FinanziamentoTab";
import RecensioniTab from "@/components/settings/RecensioniTab";
import PuntiDistintiviTab from "@/components/settings/PuntiDistintiviTab";
import PrezziTab from "@/components/settings/PrezziTab";
import OmaggiTab from "@/components/settings/OmaggiTab";
import RegistroTab from "@/components/settings/RegistroTab";
import FattureInCloudTab from "@/components/settings/FattureInCloudTab";
import ClaudeCollegatoTab from "@/components/settings/ClaudeCollegatoTab";
import { ProfiloCard, SicurezzaCard } from "@/components/settings/IlTuoAccesso";
import PageHeader from "@/components/common/PageHeader";

/**
 * Impostazioni a gruppi (blocco 4 del pacchetto): Il tuo accesso · Persone · Preventivi · Collegamenti · Controllo.
 * Prima erano dieci schede in fila. Ogni voce ha il suo `tab-<id>` (i giri e le guide lo cercano così) e si apre con
 * `?tab=<id>`: il ricarica e il «indietro» restano dove sei. Sul telefono la pagina è l'elenco dei gruppi, e ogni voce
 * apre la sua scheda con «Impostazioni» in alto per tornare. Lo stesso fra 768 e 1024, dove il menu del guscio è chiuso
 * ma il sottomenu di 240 px lascerebbe alla scheda meno di 500 px.
 */
const VOCI = [
  { gruppo: "Il tuo accesso", id: "profilo", label: "Profilo", per: "tutti", Pannello: ProfiloCard },
  { gruppo: "Il tuo accesso", id: "sicurezza", label: "Sicurezza", per: "tutti", Pannello: SicurezzaCard },
  { gruppo: "Persone", id: "utenti", label: "Collaboratori e permessi", per: "titolare", Pannello: UsersTab },
  { gruppo: "Preventivi", id: "prezzi", label: "Prezzi", per: "titolare", Pannello: PrezziTab },
  { gruppo: "Preventivi", id: "omaggi", label: "Omaggi di casa", per: "titolare", Pannello: OmaggiTab },
  { gruppo: "Preventivi", id: "punti-distintivi", label: "Punti distintivi", per: "titolare", Pannello: PuntiDistintiviTab },
  { gruppo: "Preventivi", id: "recensioni", label: "Recensioni", per: "titolare", Pannello: RecensioniTab },
  { gruppo: "Preventivi", id: "finanziamento", label: "Rate di finanziamento", per: "finanziamento", Pannello: FinanziamentoTab },
  { gruppo: "Collegamenti", id: "fatture-in-cloud", label: "Fatture in Cloud", per: "finanziamento", Pannello: FattureInCloudTab },
  { gruppo: "Collegamenti", id: "claude", label: "Claude collegato", per: "tutti", Pannello: ClaudeCollegatoTab },
  { gruppo: "Controllo", id: "registro", label: "Registro delle modifiche", per: "titolare", Pannello: RegistroTab },
];

/* Una riga che dice cosa c'è dentro, sull'elenco del telefono. Solo ciò che il codice sa dire davvero. */
const DESCRIZIONE = {
  prezzi: "Sconti d'acquisto, ricarichi e livello minimo",
  omaggi: "Cosa si offre nei preventivi e cosa parte già acceso",
  "punti-distintivi": "I punti di forza che il preventivo mette in evidenza",
  recensioni: "Quelle che compaiono nel preventivo",
  finanziamento: "Le rate che il preventivo propone",
  "fatture-in-cloud": "Il collegamento per preparare le fatture",
  claude: "Chi è collegato e cosa può leggere",
  registro: "Chi ha cambiato cosa, e quando",
  utenti: "Chi entra, di quali sedi e quali sezioni usa",
};

export default function ImpostazioniPage() {
  const { profile, factors } = useAuth();
  const [params] = useSearchParams();
  const computer = useMedia("(min-width: 1024px)"); // sotto: elenco e dettaglio uno sopra l'altro (regola del pacchetto per 768–1024)
  const isTitolare = profile?.ruolo === "titolare";
  const canFinanziamento = isTitolare || profile?.ruolo === "amministrazione";
  const visibili = VOCI.filter((v) => v.per === "tutti" || (v.per === "titolare" && isTitolare) || (v.per === "finanziamento" && canFinanziamento));
  const gruppi = [...new Set(visibili.map((v) => v.gruppo))];
  const scelta = visibili.find((v) => v.id === params.get("tab"));
  // Sul computer una voce è sempre aperta (la prima del titolare è «Collaboratori e permessi», come prima); sul telefono no.
  const aperta = scelta || (computer ? visibili.find((v) => v.id === (isTitolare ? "utenti" : "profilo")) : null);
  const attiva = (factors?.totp || []).some((f) => f.status === "verified");

  const descrizione = (v) =>
    v.id === "profilo"
      ? [profile?.ruolo ? profile.ruolo[0].toUpperCase() + profile.ruolo.slice(1) : null, profile?.email].filter(Boolean).join(" · ")
      : v.id === "sicurezza"
        ? attiva
          ? "Codice di verifica attivo"
          : "Codice di verifica non attivo"
        : DESCRIZIONE[v.id];

  const intestazione = (
    <PageHeader
      eyebrow="Configurazione"
      title="Impostazioni"
      description={
        isTitolare
          ? "Il tuo accesso, le persone, i prezzi, gli omaggi e il registro di chi ha cambiato cosa."
          : "Il tuo accesso, le rate del finanziamento e i collegamenti."
      }
    />
  );

  if (!computer) {
    // Telefono: o l'elenco dei gruppi, o una scheda sola con «Impostazioni» per tornare.
    if (aperta) {
      return (
        <section className="space-y-card" data-testid="page-impostazioni">
          <Link
            to="/impostazioni"
            data-testid="impostazioni-indietro"
            className="-ml-2 inline-flex h-11 items-center gap-1 rounded-sm px-2 font-semibold text-ink-2 transition-colors duration-150 ease-enter hover:bg-surface-3 hover:text-ink-strong"
          >
            <ChevronLeft className="size-4" aria-hidden />
            Impostazioni
          </Link>
          <aperta.Pannello />
        </section>
      );
    }
    return (
      <section className="space-y-card" data-testid="page-impostazioni">
        {intestazione}
        <nav aria-label="Impostazioni" data-testid="impostazioni-tabs" className="space-y-group">
          {gruppi.map((g) => (
            <section key={g} aria-label={g}>
              <h2 className="mb-inside text-small font-semibold text-ink-2">{g}</h2>
              <div className="overflow-hidden rounded-xl border border-line bg-surface">
                {visibili
                  .filter((v) => v.gruppo === g)
                  .map((v) => (
                    <Link
                      key={v.id}
                      to={`/impostazioni?tab=${v.id}`}
                      data-testid={`tab-${v.id}`}
                      className="flex min-h-14 items-center gap-3 border-b border-line-subtle px-4 py-2.5 last:border-b-0 hover:bg-surface-2"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-ink">{v.label}</span>
                        {descrizione(v) ? <span className="block truncate text-small text-ink-2">{descrizione(v)}</span> : null}
                      </span>
                      <ChevronRight className="size-4 shrink-0 text-ink-3" aria-hidden />
                    </Link>
                  ))}
              </div>
            </section>
          ))}
        </nav>
      </section>
    );
  }

  return (
    <section className="space-y-card" data-testid="page-impostazioni">
      {intestazione}
      <div className="grid grid-cols-[240px_minmax(0,1fr)] items-start gap-card">
        <nav aria-label="Impostazioni" data-testid="impostazioni-tabs" className="sticky top-4 space-y-group rounded-xl border border-line bg-surface p-2">
          {gruppi.map((g) => (
            <div key={g}>
              <p className="px-3 pb-1 pt-2 text-caption text-ink-3">{g}</p>
              {visibili
                .filter((v) => v.gruppo === g)
                .map((v) => (
                  <Link
                    key={v.id}
                    to={`/impostazioni?tab=${v.id}`}
                    data-testid={`tab-${v.id}`}
                    aria-current={aperta?.id === v.id ? "page" : undefined}
                    className={cn(
                      "flex h-10 items-center rounded-md px-3 transition-colors duration-150 ease-enter",
                      aperta?.id === v.id ? "bg-brand-subtle font-bold text-brand-ink" : "text-ink hover:bg-surface-2",
                    )}
                  >
                    {v.label}
                  </Link>
                ))}
            </div>
          ))}
        </nav>
        <div className="min-w-0">{aperta ? <aperta.Pannello /> : null}</div>
      </div>
    </section>
  );
}
